// OurCommitmentPage.js
import React from 'react';
import { Box, Grid, Typography, Container, Breadcrumbs, Link as MuiLink, List, ListItem, ListItemText } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const OurCommitmentPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Sustainability', path: '/sustainability' },
    { name: 'Our Commitment', path: '/sustainability/our-commitment' },
  ];

  const commitments = [
    {
      title: 'Promoting Circularity',
      description: 'We are dedicated to moving away from linear "take-make-dispose" models towards a circular economy where materials are kept in use for as long as possible, reducing the need for virgin resources.',
      details: [
        'Facilitating high-value recycling streams.',
        'Encouraging repair and reuse where feasible.',
        'Supporting innovation in sustainable material management.',
      ],
    },
    {
      title: 'Reducing Carbon Footprint',
      description: 'By optimizing the scrap supply chain and promoting recycling, we significantly contribute to lowering global carbon emissions associated with primary material production.',
      details: [
        'Minimizing transportation inefficiencies.',
        'Encouraging local sourcing and processing.',
        'Tracking and reporting carbon savings from recycled materials.',
      ],
    },
    {
      title: 'Ethical and Responsible Sourcing',
      description: 'We ensure that all materials traded on our platform adhere to strict ethical and environmental standards, preventing illicit trade and promoting responsible practices.',
      details: [
        'Implementing robust verification processes for sellers.',
        'Partnering with certified and compliant recycling facilities.',
        'Adhering to international regulations and best practices.',
      ],
    },
    {
      title: 'Community Engagement & Education',
      description: 'We believe in empowering communities and raising awareness about the importance of recycling and sustainable living through various outreach and educational programs.',
      details: [
        'Hosting workshops and seminars on recycling benefits.',
        'Collaborating with local non-profits and educational institutions.',
        'Providing resources for individuals and businesses to improve their recycling habits.',
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Container sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              crumb.path ? (
                <MuiLink component={RouterLink} to={crumb.path} key={index} underline="hover" color="text.secondary" variant="body2">
                  {crumb.name}
                </MuiLink>
              ) : (
                <Typography key={index} color="text.primary" variant="body2">{crumb.name}</Typography>
              )
            ))}
          </Breadcrumbs>
        </Box>

        <Typography variant="h3" align="center" fontWeight={700} sx={{ mb: { xs: 4, md: 6 }, color: 'text.primary' }}>
          Our Commitment
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto', color: 'text.secondary' }}>
          At the core of our operations lies a deep commitment to environmental stewardship and responsible business practices. We strive to make a tangible difference through every transaction facilitated on our platform.
        </Typography>

        <Box sx={{ mt: 6 }}>
          {commitments.map((commitment, index) => (
            <Box
              key={index}
              sx={{
                mb: 6,
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                background: 'background.paper',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'grey.200',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 16px 32px rgba(0,0,0,0.12)',
                },
              }}
            >
              <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: 'primary.dark' }}>
                {commitment.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
                {commitment.description}
              </Typography>

              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="h6" fontWeight={600} sx={{ color: 'secondary.main', mb: 1.5 }}>
                      Key Initiatives:
                    </Typography>
                    <List dense disablePadding>
                      {commitment.details.map((detail, detailIndex) => (
                        <ListItem key={detailIndex} disablePadding sx={{ py: 0.5 }}>
                          <ListItemText primary={detail} primaryTypographyProps={{ variant: 'body1', color: 'text.secondary' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default OurCommitmentPage;
